<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useHead } from '@vueuse/head'

import { useClient } from '@base/api/useClient'

import LeaderboardTable from '@base/components/LeaderboardTable/LeaderboardTable.vue'

import { useUserStore } from '@base/store/userStore'
import { playerTitle } from '@player/_lib/playerTitle'
import { GET_LEADERBOARD_QUERY } from '@player/leaderboard/api/queries/getLeaderboard'
import { LeaderboardTable as LeaderboardTableType } from '@base/_types/LeaderboardTable'
import {
  getLeaderboard,
  getLeaderboard_getLeaderboard_GetLeaderboardOutput_leaderboard
} from '__generated__/getLeaderboard'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'
import { useToastsStore } from '@base/store/toastsStore'

// Static Variables
const client = useClient()
const userStore = useUserStore()
const toastsStore = useToastsStore()

useHead({
  title: playerTitle('Leaderboard')
})

// Outputs
const loading = ref<boolean>(false)
const serverError = ref<boolean>(false)
const leaderboard = ref<LeaderboardTableType[]>([])

// Helper Functions
const reset = () => {
  loading.value = false
  serverError.value = false
}

// Query Functions
const fetchData = async () => {
  reset()

  function adapt(
    entry: getLeaderboard_getLeaderboard_GetLeaderboardOutput_leaderboard
  ): LeaderboardTableType {
    return {
      username: entry.username,
      rank: entry.rank,
      points: Math.max(entry.points, 0),
      userId: '',
      avatar: entry.picture ? ugcImageSrc(entry.picture)[0] : undefined,
      isUser: entry.username === userStore.username
    }
  }

  // Fetch Data
  try {
    loading.value = true

    const { data } = await client.query<getLeaderboard>({
      query: GET_LEADERBOARD_QUERY,
      variables: {
        userId: userStore.id
      }
    })

    if (data.getLeaderboard.__typename === 'GetLeaderboardOutput') {
      leaderboard.value = data.getLeaderboard.leaderboard.map(adapt)
    } else {
      serverError.value = true
    }
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}

watch(serverError, (isError) => {
  if (isError) {
    toastsStore.pushErrorToast(
      'Something went wrong fetching the leaderboard, please try again later.'
    )
  }
})

// Fetch data when the view is first rendered
await fetchData()
</script>

<template>
  <div class="leaderboard">
    <div class="grid__row">
      <div class="grid__column grid__column--12 margin-bottom--6">
        <h3 class="heading--h3">Leaderboard</h3>
      </div>
      <LeaderboardTable :users="leaderboard" />
    </div>
  </div>
</template>

<style lang="scss">
.base-template__main {
  overflow: visible !important;
}
</style>
